/*
 * DWM global header behaviour.
 *
 *   <dwm-header>    sticky/glass state (IntersectionObserver sentinel, no scroll listener) and --dwm-header-height
 *   <dwm-nav-item>  disclosure dropdown: toggle button (keyboard + click), hover intent on fine pointers,
 *                   Escape returns focus to the toggle, closes on focus-out / outside click, one open at a time
 *   <dwm-drawer>    mobile menu built on Impact's DialogElement (focus trap, Escape, outside click, scroll lock,
 *                   aria-expanded on the trigger, focus returns to the trigger on close)
 *
 * Without JavaScript every page stays reachable: hub links work, desktop panels open on focus (see dwm-header.css)
 * and the footer contains the full navigation.
 */
import { DialogElement } from 'theme';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
const desktopQuery = window.matchMedia('(min-width: 1280px)');

/* ------------------------------------------------------------------------------------------------------------------
 * Header
 * ---------------------------------------------------------------------------------------------------------------- */

class DwmHeader extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    this.resizeObserver = new ResizeObserver(([entry]) => {
      document.documentElement.style.setProperty('--dwm-header-height', `${Math.round(entry.borderBoxSize[0].blockSize)}px`);
    });
    this.resizeObserver.observe(this);

    if (this.hasAttribute('data-sticky')) {
      // A 1px sentinel just below the top of the page: once it leaves the viewport the page has scrolled.
      this.sentinel = document.createElement('div');
      this.sentinel.className = 'dwm-header-sentinel';
      this.sentinel.setAttribute('aria-hidden', 'true');
      document.body.prepend(this.sentinel);

      this.intersectionObserver = new IntersectionObserver(([entry]) => {
        this.toggleAttribute('data-scrolled', !entry.isIntersecting);
      });
      this.intersectionObserver.observe(this.sentinel);
    }
  }

  disconnectedCallback() {
    this.abortController.abort();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    this.sentinel?.remove();
  }
}

/* ------------------------------------------------------------------------------------------------------------------
 * Dropdown (disclosure navigation pattern)
 * ---------------------------------------------------------------------------------------------------------------- */

const HOVER_OPEN_DELAY = 80;
const HOVER_CLOSE_DELAY = 220;

class DwmNavItem extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.toggleButton = this.querySelector('.dwm-nav__toggle');
    this.panel = this.querySelector('.dwm-nav__panel');

    this.toggleButton.addEventListener('click', () => (this.isOpen && this.openedBy !== 'hover' ? this.close() : this.open('toggle')), { signal });
    this.addEventListener('keydown', (event) => this.onKeyDown(event), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) this.close();
    }, { signal });
    this.addEventListener('pointerenter', () => this.onPointerEnter(), { signal });
    this.addEventListener('pointerleave', () => this.onPointerLeave(), { signal });
    document.addEventListener('click', (event) => {
      if (this.isOpen && !this.contains(event.target)) this.close();
    }, { signal });
    document.addEventListener('dwm-nav:open', (event) => {
      if (event.detail !== this) this.close();
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController.abort();
    clearTimeout(this.hoverTimer);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open(openedBy = 'toggle') {
    clearTimeout(this.hoverTimer);
    // A click or keypress "pins" a panel that was opened by hover, so it no longer closes on pointer leave.
    this.openedBy = openedBy === 'toggle' ? 'toggle' : this.openedBy || openedBy;
    if (this.isOpen) return;
    this.setAttribute('open', '');
    this.toggleButton.setAttribute('aria-expanded', 'true');
    document.dispatchEvent(new CustomEvent('dwm-nav:open', { detail: this }));
  }

  close({ returnFocus = false } = {}) {
    clearTimeout(this.hoverTimer);
    if (!this.isOpen) return;
    this.openedBy = null;
    this.removeAttribute('open');
    this.toggleButton.setAttribute('aria-expanded', 'false');
    if (returnFocus) this.toggleButton.focus();
  }

  onKeyDown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      event.stopPropagation();
      this.close({ returnFocus: this.panel.contains(document.activeElement) || document.activeElement === this.toggleButton });
    }
  }

  onPointerEnter() {
    if (!finePointer.matches) return;
    clearTimeout(this.hoverTimer);
    this.hoverTimer = setTimeout(() => this.open('hover'), HOVER_OPEN_DELAY);
  }

  onPointerLeave() {
    if (!finePointer.matches) return;
    clearTimeout(this.hoverTimer);
    // Panels opened with the toggle (mouse click or keyboard) stay open until explicitly closed.
    if (this.isOpen && this.openedBy !== 'hover') return;
    this.hoverTimer = setTimeout(() => this.close(), HOVER_CLOSE_DELAY);
  }
}

/* ------------------------------------------------------------------------------------------------------------------
 * Mobile drawer
 * ---------------------------------------------------------------------------------------------------------------- */

class DwmDrawer extends DialogElement {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this._abortController;

    this.querySelectorAll('[data-dwm-drawer-close], [data-dwm-drawer-close-overlay]').forEach((element) => {
      element.addEventListener('click', () => this.hide(), { signal });
    });

    // Following a link inside the menu (e.g. an anchor on the same page) should not leave the drawer open.
    this.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) this.hide();
    }, { signal });

    desktopQuery.addEventListener('change', (event) => event.matches && this.hide(), { signal });
  }

  get shouldLock() {
    return true;
  }

  _animate(keyframes) {
    const panel = this.querySelector('.dwm-drawer__panel');
    const overlay = this.querySelector('.dwm-drawer__overlay');

    [panel, overlay].forEach((element) => element.getAnimations().forEach((animation) => animation.cancel()));

    if (reducedMotion.matches) {
      return Promise.resolve();
    }

    const options = { duration: 320, easing: 'cubic-bezier(.2, .8, .2, 1)', fill: 'both' };
    return Promise.all([
      panel.animate(keyframes.panel, options).finished,
      overlay.animate(keyframes.overlay, options).finished,
    ]).catch(() => {});
  }

  _showTransition() {
    return this._animate({
      panel: [{ transform: 'translateX(100%)' }, { transform: 'translateX(0)' }],
      overlay: [{ opacity: 0 }, { opacity: 1 }],
    });
  }

  _hideTransition() {
    this.classList.add('is-closing');
    return this._animate({
      panel: [{ transform: 'translateX(0)' }, { transform: 'translateX(100%)' }],
      overlay: [{ opacity: 1 }, { opacity: 0 }],
    }).then(() => this.classList.remove('is-closing'));
  }
}

if (!customElements.get('dwm-header')) customElements.define('dwm-header', DwmHeader);
if (!customElements.get('dwm-nav-item')) customElements.define('dwm-nav-item', DwmNavItem);
if (!customElements.get('dwm-drawer')) customElements.define('dwm-drawer', DwmDrawer);
